import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useValuesVisibility } from "@/hooks/use-values-visibility";
import type { DueAlert } from "@/hooks/use-due-alerts";
import {
  arrayMove,
} from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";

export function useDashboardState() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [totalBalance, setTotalBalance] = useState(0);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedCreditCardId, setSelectedCreditCardId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
  const [selectedAccountForImport, setSelectedAccountForImport] = useState<string | null>(null);
  const [creditCardRefreshKey, setCreditCardRefreshKey] = useState(0);
  const [editingTransaction, setEditingTransaction] = useState<any | null>(null);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [defaultTransactionAccountId, setDefaultTransactionAccountId] = useState<string | null>(null);

  const { mask } = useValuesVisibility();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [{ data: accountsData, error }, { data: goalsData }] = await Promise.all([
        supabase
          .from("accounts")
          .select("*")
          .eq("is_active", true)
          .eq("archived", false)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true }),
        supabase
          .from("goals")
          .select("current_amount")
          .eq("is_active", true),
      ]);

      if (error) throw error;

      setAccounts(accountsData || []);

      const accountsTotal = (accountsData || [])
        .filter(account => account.type !== "credit_card")
        .reduce((sum, account) => sum + Number(account.balance), 0);
      const goalsTotal = (goalsData || []).reduce((sum, goal) => sum + Number(goal.current_amount), 0);
      setTotalBalance(accountsTotal + goalsTotal);
      // Refresh dashboard charts/analytics
      queryClient.invalidateQueries({ queryKey: ["dashboard-analytics"] });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar dados",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccountDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const bankAccounts = accounts.filter(a => a.type !== "credit_card");
    const oldIndex = bankAccounts.findIndex(a => a.id === active.id);
    const newIndex = bankAccounts.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(bankAccounts, oldIndex, newIndex);
    const creditCards = accounts.filter(a => a.type === "credit_card");
    setAccounts([...reordered, ...creditCards]);

    try {
      for (let i = 0; i < reordered.length; i++) {
        await supabase
          .from("accounts")
          .update({ sort_order: i })
          .eq("id", reordered[i].id);
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Erro ao reordenar", description: error.message });
      fetchData();
    }
  };

  const handleCreditCardDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const creditCards = accounts.filter(a => a.type === "credit_card");
    const oldIndex = creditCards.findIndex(a => a.id === active.id);
    const newIndex = creditCards.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(creditCards, oldIndex, newIndex);
    const bankAccounts = accounts.filter(a => a.type !== "credit_card");
    setAccounts([...bankAccounts, ...reordered]);

    try {
      for (let i = 0; i < reordered.length; i++) {
        await supabase
          .from("accounts")
          .update({ sort_order: i })
          .eq("id", reordered[i].id);
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Erro ao reordenar", description: error.message });
      fetchData();
    }
  };

  const handleViewDueBill = (alert: DueAlert) => {
    setEditingTransaction({
      id: alert.id,
      description: alert.description,
      amount: alert.amount,
      type: alert.type,
      transaction_date: alert.transaction_date,
      account_id: alert.account_id,
      category_id: alert.category_id,
      is_paid: alert.is_paid,
      is_recurring: alert.is_recurring,
      is_fixed: alert.is_fixed,
    });
    setShowEditTransactionModal(true);
  };

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
    return mask(formatted);
  };

  const bankAccounts = accounts.filter(a => a.type !== "credit_card");
  const creditCards = accounts.filter(a => a.type === "credit_card");

  return {
    loading,
    totalBalance,
    accounts,
    bankAccounts,
    creditCards,
    showTransactionModal,
    setShowTransactionModal,
    showImportModal,
    setShowImportModal,
    showInvoiceModal,
    setShowInvoiceModal,
    showAccountModal,
    setShowAccountModal,
    selectedCreditCardId,
    setSelectedCreditCardId,
    selectedAccount,
    setSelectedAccount,
    selectedAccountForImport,
    setSelectedAccountForImport,
    creditCardRefreshKey,
    setCreditCardRefreshKey,
    editingTransaction,
    setEditingTransaction,
    showEditTransactionModal,
    setShowEditTransactionModal,
    defaultTransactionAccountId,
    setDefaultTransactionAccountId,
    fetchData,
    handleAccountDragEnd,
    handleCreditCardDragEnd,
    handleViewDueBill,
    formatCurrency,
  };
}
